import moment, { Duration } from "moment";

/**
 * Checks if a date is valid (not undefined, after epoch, and year > 1)
 * @param date - The date to validate
 * @returns true if the date is valid, false otherwise
 */
const isValidDate = (date: Date | undefined): boolean => {
    // new Date(0)=Thu Jan 01 1970 01:00:00 GMT+0100 (heure normale d'Europe centrale)
    // date.toString().startsWith('0001') when Date is given by .Net
    return new Date(date!) > new Date(0) && new Date(date!).getFullYear() > 1;
}

/**
 * Calculates the duration between a start date and now
 * @param startDate - The start date to calculate from
 * @returns A moment Duration object representing the time difference
 */
export const getDiffDuration = (startDate: Date): Duration => {
    /* FIX cutter.lisA_LastReportInDays */
    if (!isValidDate(startDate)) return moment.duration(-1, 'day');
    const startDateAsMoment = moment(startDate);
    const timeEndLisa = moment(moment.now());
    const diff = timeEndLisa.diff(startDateAsMoment);
    return moment.duration(diff);
}

/**
 * Formats a date into a localized string with time
 * @param date - The date to format
 * @returns A formatted date string or 'No data' if invalid
 */
export const getFormatedStringFromDate = (date: Date | undefined): string => {
    if (!isValidDate(date)) {
        return 'No data';
    }
    
    // Convert to proper Date object
    let dateObj: Date;
    const dateStr = date!.toString();
    
    if (dateStr.includes('+')) {
        // Already has timezone info
        dateObj = new Date(date!);
    } else {
        // Add UTC timezone
        dateObj = new Date(`${date}Z`);
    }
    
    // Format the date
    return dateObj
        .toLocaleDateString(navigator.language, { 
            // year: 'numeric',
            // month: 'short', 
            // day: 'numeric',
            hour: '2-digit', 
            minute: '2-digit' 
        })
        .replace(' 00:00', '');
}

/**
 * Gets a human-readable duration string from a date
 * @param date - The date to calculate duration from
 * @returns A formatted duration string or 'No data' if invalid
 */
export const getDurationStringFromDate = (date: Date): string => {
    return isValidDate(date) ? getDurationStringFromDays(getDiffDuration(new Date(date)).asDays()).trim() : 'No data';
}

/**
 * Converts a number of days into a human-readable string
 * @param numberOfDays - The number of days to convert
 * @returns A formatted string like "1y 2m 15d" or special cases like "Today"/"Yesterday"
 */
export const getDurationStringFromDays = (numberOfDays?: number): string => {
    if (numberOfDays === undefined) return 'No data';

    if (numberOfDays < 1) return 'Today';
    if (numberOfDays < 2) return 'Yesterday';
    if (numberOfDays > 36500) return 'No data'; // > 100 years → sentinel date (e.g. 1900-01-01 or DateTime.MinValue)

    const years = Math.floor(numberOfDays / 365);
    const months = Math.floor(numberOfDays % 365 / 30);
    const days = Math.floor(numberOfDays % 365 % 30);

    return [years === 0 ? '' : (years.toString() + 'y'), months === 0 ? '' : (months.toString() + 'm'), days === 0 ? '' : (days.toString() + 'd')].join(' ');
}

/**
 * Generates a zip file link if the date is within the valid range (last 60 days)
 * @param zipDate - The date the zip file was created
 * @param zipFileName - The name of the zip file
 * @returns The URL to the zip file or undefined if outside valid date range
 */
export const getZipFileLink = (zipFileName: string, zipStoredOnDisk: boolean): string | undefined => {
    return zipStoredOnDisk ? `https://cutter-report.lectra.com/${zipFileName}.zip` : undefined;
}


